import React, {
  FunctionComponent,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ThemeProvider } from '@mui/material';

import { checkIsApkInfoNonEmpty } from '../../../app-store/utils/check-is-apk-info-non-empty.util';
import { ReactComponent as ApkIcon } from '../../../shared/assets/icons/releases-icons/apk-upload-icon.svg';
import { ReactComponent as CloseIcon } from '../../../shared/assets/icons/releases-icons/close-apk.svg';
import { ReactComponent as DeleteIcon } from '../../../shared/assets/icons/releases-icons/delete.svg';
import Box from '../../components/ui/Box/Box';
import Button from '../../components/ui/Button/Button';
import Paper from '../../components/ui/Paper/Paper';
import Typography from '../../components/ui/Typography/Typography';
import { FileMetadataTypesEnum } from '../../enums/file-metadata-types.enum';
import { DragAndDropTheme } from './drag-and-drop-theme/drag-and-drop.theme';
import { useHandleApkFileChange } from './hooks/use-handle-apk-file-change/use-handle-apk-file-change.hook';
import { useHandleApkFileDrop } from './hooks/use-handle-apk-file-drop/use-handle-apk-file-drop.hook';
import { useHandleFileChange } from './hooks/use-handle-file-change/use-handle-file-change.hook';
import { useHandleRemoveFile } from './hooks/use-handle-remove-file/use-handle-remove-file.hook';
import { DragAndDropProps } from './models/drag-and-drop.model';
import { callbackFunction } from './util/callback-function/callback-function.util';
import { fileRenderer } from './util/file-renderer/file-renderer.util';
import { handleDragEvent } from './util/handle-drag-event/handle-drag-event.util';
import { mapIdFileToFile } from './util/map-idFile-to-file/map-id-file-to-file.util';
import { triggerUploadOnButtonClick } from './util/trigger-upload-on-click/trigger-upload-on-click.util';

const DragAndDrop: FunctionComponent<DragAndDropProps> = (
  props: PropsWithChildren<DragAndDropProps>
): ReactElement => {
  const [dragOver, setDragOver] = useState(false);
  const [files, setFiles] = useState<File[]>(
    props.files ? props.files.map(mapIdFileToFile) : []
  );

  const isUploadedFileImage =
    props.previewType === FileMetadataTypesEnum.ICON ||
    props.previewType === FileMetadataTypesEnum.SCREENSHOT;

  const isUploadedFileApkOrDocument =
    props.previewType === FileMetadataTypesEnum.APK ||
    props.previewType === FileMetadataTypesEnum.DOCUMENT;

  const fileChangeHandler =
    props.fileType === FileMetadataTypesEnum.APK
      ? useHandleApkFileChange
      : useHandleFileChange;

  const fileDropHandler =
    props.fileType === FileMetadataTypesEnum.APK
      ? useHandleApkFileDrop
      : useHandleFileChange;

  useEffect(() => {
    if (props.files) {
      setFiles(props.files.map(mapIdFileToFile));
    }
  }, [props.files]);

  const handleFileChange = fileChangeHandler(
    callbackFunction(setFiles, props),
    props
  );

  const handleFileDrop = fileDropHandler(
    callbackFunction(setFiles, props),
    props,
    handleDragEvent(setDragOver, false)
  );

  const handleRemoveFile = useHandleRemoveFile(props, setFiles);

  const renderedFiles: JSX.Element[] = useMemo(
    () =>
      files.length > 0
        ? files.map((file, index) => (
            <Box
              className={
                isUploadedFileApkOrDocument
                  ? 'drag-and-drop--apk'
                  : 'drag-and-drop--style'
              }
              key={file.type + index + file.name + file.size}
            >
              {isUploadedFileImage && (
                <>
                  <DeleteIcon
                    onClick={event => handleRemoveFile(event, index)}
                    className={'icon-close icon-image svg-icon'}
                    title="Delete"
                  />

                  <img
                    key={
                      'drag-and-drop--img' +
                      file.type +
                      index +
                      file.name +
                      file.size
                    }
                    className={'drag-and-drop--img'}
                    src={fileRenderer(file)}
                    alt={file.name}
                  />
                </>
              )}

              {isUploadedFileApkOrDocument && (
                <>
                  <CloseIcon
                    className={'icon-close svg-icon'}
                    onClick={event => handleRemoveFile(event, index)}
                  />

                  <Typography className={'drag-and-drop--apk-helper-text'}>
                    {file.name}
                  </Typography>
                </>
              )}
            </Box>
          ))
        : [],
    [files]
  );

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <ThemeProvider theme={DragAndDropTheme}>
        <Paper
          classes={{
            root: 'drag-and-drop--wrapper',
          }}
        >
          <Box
            className={`drag-and-drop--box${
              props.hasError ? ' has-error' : ''
            }${dragOver ? ' is-dragging' : ''}`}
            onDragEnter={handleDragEvent(setDragOver, true)}
            onDragOver={handleDragEvent(setDragOver, true)}
            onDragLeave={handleDragEvent(setDragOver, false)}
            onDrop={handleFileDrop}
          >
            <input
              type="file"
              key={`${files.length}upload-file-input${props.name}`}
              accept={props.fileTypeAccepted}
              multiple={props.multiple}
              style={{ display: 'none' }}
              ref={inputRef}
              onChange={handleFileChange}
            />

            <Typography className={'drag-and-drop--typography'}>
              {props.label}
              {props.isRequired && ' *'}
            </Typography>

            {files.length > 0 && (
              <Box
                className={
                  isUploadedFileApkOrDocument
                    ? 'drag-and-drop--apk-files'
                    : 'drag-and-drop--files'
                }
                sx={{
                  ...(!props.multiple && { justifyContent: 'center' }),
                }}
              >
                {renderedFiles}
              </Box>
            )}
            <Box
              className={
                props.previewType === FileMetadataTypesEnum.APK
                  ? 'drag-and-drop--label-apk'
                  : 'drag-and-drop--label'
              }
            >
              {props.previewType === FileMetadataTypesEnum.APK && (
                <ApkIcon className={'file-upload-icon svg-icon'} />
              )}
              {props.previewType !== FileMetadataTypesEnum.APK &&
                files.length === 0 && (
                  <ApkIcon className={'file-upload-icon svg-icon'} />
                )}

              <Typography className={'drag-and-drop--helperText'}>
                {props.helperText}
              </Typography>

              <Button
                className={'drag-and-drop--button open-upload-prompt'}
                onClick={triggerUploadOnButtonClick(inputRef)}
                disabled={
                  props.apkInfo && !checkIsApkInfoNonEmpty(props.apkInfo)
                }
              >
                {props.buttonLabel}
              </Button>
            </Box>
          </Box>
        </Paper>
      </ThemeProvider>
    </>
  );
};

export default DragAndDrop;
